import api from "../../lib/api";
import { downloadBlob } from "../../lib/download";
import { fmtBRL } from "../../lib/format";

export const downloadDanfe = async (note) => {
  const res = await api.get(`/nfe/notes/${note.id}/danfe`, { responseType: "blob" });
  downloadBlob(res.data, `danfe_${note.order_code || "avulsa"}_${note.numero || note.ref}.pdf`);
};

export const downloadXml = async (note) => {
  const res = await api.get(`/nfe/notes/${note.id}/xml`, { responseType: "blob" });
  downloadBlob(res.data, `nfe_${note.numero || note.ref}.xml`);
};

export const refreshNote = async (note) => {
  const { data } = await api.get(`/nfe/notes/${note.id}`);
  return data;
};

export const cancelNote = async (note, justificativa) => {
  const { data } = await api.post(`/nfe/notes/${note.id}/cancel`, { justificativa });
  return data;
};

export const downloadCcePdf = async (note, seq) => {
  const res = await api.get(`/nfe/notes/${note.id}/cce/${seq}/pdf`, { responseType: "blob" });
  downloadBlob(res.data, `cce_${seq}_nfe_${note.numero || note.ref}.pdf`);
};

export const downloadCceXml = async (note, seq) => {
  const res = await api.get(`/nfe/notes/${note.id}/cce/${seq}/xml`, { responseType: "blob" });
  downloadBlob(res.data, `cce_${seq}_nfe_${note.numero || note.ref}.xml`);
};

export const latestCceSeq = (note) => {
  const seqs = (note.cartas_correcao || []).map((c) => Number(c.seq) || 0);
  return seqs.length ? Math.max(...seqs) : null;
};

export const STATUS_LABEL = {
  autorizado: "Autorizada", processando_autorizacao: "Processando", enviando: "Enviando",
  desconhecido: "Confirmar envio", cancelado: "Cancelada", erro_autorizacao: "Rejeitada",
  nao_encontrado: "Não recebida", erro_cancelamento: "Autorizada (cancelamento recusado)",
};

export const isAutorizada = (status) => status === "autorizado" || status === "erro_cancelamento";

export const STATUS_STYLE = {
  autorizado: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  erro_cancelamento: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  processando_autorizacao: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  enviando: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  desconhecido: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  cancelado: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
};

export const StatusChip = ({ status, corrected }) => (
  <span className="inline-flex items-center gap-1.5">
    <span className={`text-[11px] font-medium border rounded-full px-2.5 py-0.5 ${STATUS_STYLE[status] || "bg-rose-500/15 text-rose-400 border-rose-500/30"}`}>
      {STATUS_LABEL[status] || status}
    </span>
    {corrected > 0 && (
      <span className="text-[11px] font-medium border rounded-full px-2 py-0.5 bg-sky-500/15 text-sky-300 border-sky-500/30"
        title={`${corrected} carta(s) de correção emitida(s)`}>
        {corrected} CC-e
      </span>
    )}
  </span>
);

export const validateNfeForm = (form) => {
  const errs = [];
  const dg = (s) => String(s || "").replace(/\D/g, "");
  const nm = (v) => parseFloat(String(v ?? "").replace(",", ".")) || 0;
  const d = form.destinatario;
  if (!d.nome?.trim()) errs.push("Destinatário: informe o nome");
  if (![11, 14].includes(dg(d.cnpj_cpf).length)) errs.push("Destinatário: CNPJ/CPF inválido");
  if (!d.logradouro?.trim()) errs.push("Destinatário: informe o endereço");
  if (!d.municipio?.trim()) errs.push("Destinatário: informe o município");
  if ((d.uf || "").trim().length !== 2) errs.push("Destinatário: informe a UF");
  if (dg(d.cep).length !== 8) errs.push("Destinatário: CEP inválido (8 dígitos)");
  form.itens.forEach((it, i) => {
    if (!it.descricao?.trim()) errs.push(`Produto ${i + 1}: descrição obrigatória`);
    if (dg(it.ncm).length !== 8) errs.push(`Produto ${i + 1}: NCM deve ter 8 dígitos`);
    if (dg(it.cfop).length !== 4) errs.push(`Produto ${i + 1}: CFOP deve ter 4 dígitos`);
    if (nm(it.quantidade) <= 0 || nm(it.valor_unitario) <= 0) errs.push(`Produto ${i + 1}: quantidade e valor unitário devem ser maiores que zero`);
  });
  const total = nm(form.impostos.valor_total);
  const dups = form.duplicatas.filter((dp) => nm(dp.valor) > 0);
  if (dups.length) {
    const soma = Math.round(dups.reduce((s, dp) => s + nm(dp.valor), 0) * 100) / 100;
    if (Math.abs(soma - total) > 0.01) errs.push(`Soma das duplicatas (${fmtBRL(soma)}) difere do total da nota (${fmtBRL(total)})`);
    if (dups.some((dp) => !dp.vencimento)) errs.push("Informe o vencimento de todas as duplicatas");
  }
  return errs;
};
